/**
 * reducers/review-queues.ts
 * desc: client reviews queues
 */

import type { Reducer } from '@reduxjs/toolkit';
import { getIntlDateTime, sortAz } from '../../utilities';
import journeysConstants from '../../modules/JourneysDetail/constants';
import type { ReviewQueuesAction } from '../actions/review-queues';
import type { MessageReviewItemDto, ReviewItemRequestFieldDto, ReviewItemResponseFieldDto, ReviewQueueDto } from '../../types/api/review-queues';
import type { ComponentInfo } from '../../types/api/components';

// a queue as the list and detail fetches store it; create/update keep the bare DTO
export type ReviewQueue = ReviewQueueDto & { availableItemCount?: number };

// the current message review item with its fields split into the common ones and the reserved
// ones keyed by name; the reserved created-date is replaced by its formatted text
export interface EnrichedReviewItem extends MessageReviewItemDto {
    commonRequestFields: ReviewItemRequestFieldDto[];
    reservedRequestFieldsMap: Record<string, ReviewItemRequestFieldDto | string | null | undefined>;
    commonResponseFields: ReviewItemResponseFieldDto[];
    reservedResponseFieldsMap: Record<string, ReviewItemResponseFieldDto>;
}

export interface ReviewQueuesState {
    isLoading: {
        createReviewQueue: boolean;
        deleteReviewQueue: boolean;
        getReviewItemNext: boolean;
        getReviewQueue: boolean;
        getReviewQueueUsage: boolean;
        getReviewQueues: boolean;
        updateReviewItem: boolean;
        updateReviewQueue: boolean;
    };
    // empty objects until an item / a queue arrives
    itemDetail: EnrichedReviewItem | Record<string, never>;
    queueDetail: ReviewQueue | Record<string, never>;
    queueList: ReviewQueue[];
    queueUsage: ComponentInfo[];
}

const INITIAL_STATE: ReviewQueuesState = {
    isLoading: {
        createReviewQueue: false,
        deleteReviewQueue: false,
        getReviewItemNext: false,
        getReviewQueue: false,
        getReviewQueueUsage: false,
        getReviewQueues: false,
        updateReviewItem: false,
        updateReviewQueue: false,
    },
    itemDetail: {},
    queueDetail: {},
    queueList: [],
    queueUsage: [],
};


function reviewQueuesReducer(state: ReviewQueuesState = INITIAL_STATE, action: ReviewQueuesAction): ReviewQueuesState {
    switch (action.type) {
        // CLEAR Review Queues
        case 'CLEAR_REVIEW_QUEUES':
            return _clearReviewQueues(state);

        // CREATE Review Queue (Instance)
        case 'CREATE_REVIEW_QUEUE_ERROR':
            return _createReviewQueueError(state);
        case 'CREATE_REVIEW_QUEUE_REQUEST':
            return _createReviewQueueRequest(state);
        case 'CREATE_REVIEW_QUEUE_SUCCESS':
            return _createReviewQueueSuccess(state, action.payload);

        // DELETE Review Queue (Instance)
        case 'DELETE_REVIEW_QUEUE_ERROR':
            return _deleteReviewQueueError(state);
        case 'DELETE_REVIEW_QUEUE_REQUEST':
            return _deleteReviewQueueRequest(state);
        case 'DELETE_REVIEW_QUEUE_SUCCESS':
            return _deleteReviewQueueSuccess(state, action.payload);

        // GET Review Item Next (Instance)
        case 'GET_REVIEW_ITEM_NEXT_ERROR':
            return _getReviewItemNextError(state);
        case 'GET_REVIEW_ITEM_NEXT_REQUEST':
            return _getReviewItemNextRequest(state);
        case 'GET_REVIEW_ITEM_NEXT_SUCCESS':
            return _getReviewItemNextSuccess(state, action.payload);

        // GET Review Queue (Instance)
        case 'GET_REVIEW_QUEUE_ERROR':
            return _getReviewQueueError(state);
        case 'GET_REVIEW_QUEUE_REQUEST':
            return _getReviewQueueRequest(state);
        case 'GET_REVIEW_QUEUE_SUCCESS':
            return _getReviewQueueSuccess(state, action.payload);

        // GET Review Queue Usage (Instance)
        case 'GET_REVIEW_QUEUE_USAGE_ERROR':
            return _getReviewQueueUsageError(state);
        case 'GET_REVIEW_QUEUE_USAGE_REQUEST':
            return _getReviewQueueUsageRequest(state);
        case 'GET_REVIEW_QUEUE_USAGE_SUCCESS':
            return _getReviewQueueUsageSuccess(state, action.payload);

        // GET Review Queues (Collection)
        case 'GET_REVIEW_QUEUES_ERROR':
            return _getReviewQueuesError(state);
        case 'GET_REVIEW_QUEUES_REQUEST':
            return _getReviewQueuesRequest(state);
        case 'GET_REVIEW_QUEUES_SUCCESS':
            return _getReviewQueuesSuccess(state, action.payload);

        // UPDATE Review Item (Instance)
        case 'UPDATE_REVIEW_ITEM_ERROR':
            return _updateReviewItemError(state);
        case 'UPDATE_REVIEW_ITEM_REQUEST':
            return _updateReviewItemRequest(state);
        case 'UPDATE_REVIEW_ITEM_SUCCESS':
            return _updateReviewItemSuccess(state, action.payload);

        // UPDATE Review Queue (Instance)
        case 'UPDATE_REVIEW_QUEUE_ERROR':
            return _updateReviewQueueError(state);
        case 'UPDATE_REVIEW_QUEUE_REQUEST':
            return _updateReviewQueueRequest(state);
        case 'UPDATE_REVIEW_QUEUE_SUCCESS':
            return _updateReviewQueueSuccess(state, action.payload);

        default:
            return state;
    }
}



//*************************************************
//*****   CLEAR Review Queues   *******************
//*************************************************
function _clearReviewQueues(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getReviewItemNext: false,
        },
        itemDetail: {},
        queueDetail: {},
    };
}



//*************************************************
//*****   CREATE Review Queue (Instance)   ********
//*************************************************
function _createReviewQueueError(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createReviewQueue: false,
        },
    };
}

function _createReviewQueueRequest(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createReviewQueue: true,
        },
    };
}

function _createReviewQueueSuccess(state: ReviewQueuesState, payload: ReviewQueueDto): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createReviewQueue: false,
        },
        queueDetail: payload,
        queueList: sortAz([ ...state.queueList, payload ], 'name'),
    };
}



//*************************************************
//*****   DELETE Review Queue (Instance)   ********
//*************************************************
function _deleteReviewQueueError(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteReviewQueue: false,
        },
    };
}

function _deleteReviewQueueRequest(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteReviewQueue: true,
        },
    };
}

function _deleteReviewQueueSuccess(state: ReviewQueuesState, reviewQueueId: string): ReviewQueuesState {
    // create new list wo deleted queue
    const [ ...newQueueList ] = state.queueList;
    const deletedIndex = state.queueList.findIndex(x => x.id === reviewQueueId);
    if (deletedIndex > -1) newQueueList.splice(deletedIndex, 1);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteReviewQueue: false,
        },
        queueList: newQueueList,
    };
}



//*************************************************
//*****   GET Review Item Next (Instance)   *******
//*************************************************
function _getReviewItemNextError(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getReviewItemNext: false,
        },
    };
}

function _getReviewItemNextRequest(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getReviewItemNext: true,
        },
    };
}

function _getReviewItemNextSuccess(state: ReviewQueuesState, payload: MessageReviewItemDto): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getReviewItemNext: false,
        },
        itemDetail: _enrichReviewItem(payload),
    };
}



//*************************************************
//*****   GET Review Queue (Instance)   ***********
//*************************************************
function _getReviewQueueError(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getReviewQueue: false,
        },
    };
}

function _getReviewQueueRequest(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getReviewQueue: true,
        },
    };
}

function _getReviewQueueSuccess(state: ReviewQueuesState, payload: ReviewQueueDto): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getReviewQueue: false,
        },
        queueDetail: _enrichReviewQueue(payload),
    };
}



//*************************************************
//*****   GET Review Queue Usage (Instance)   *****
//*************************************************
function _getReviewQueueUsageError(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getReviewQueueUsage: false,
        },
    };
}

function _getReviewQueueUsageRequest(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getReviewQueueUsage: true,
        },
    };
}

function _getReviewQueueUsageSuccess(state: ReviewQueuesState, payload: ComponentInfo[]): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getReviewQueueUsage: false,
        },
        queueUsage: payload,
    };
}



//*************************************************
//*****   GET Review Queues (Collection)   *******
//*************************************************
function _getReviewQueuesError(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getReviewQueues: false,
        },
    };
}

function _getReviewQueuesRequest(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getReviewQueues: true,
        },
    };
}

function _getReviewQueuesSuccess(state: ReviewQueuesState, payload: ReviewQueueDto[]): ReviewQueuesState {
    const enrichedList = _enrichReviewQueueList(payload);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getReviewQueues: false,
        },
        queueList: sortAz(enrichedList, 'name'),
    };
}



//*************************************************
//*****   UPDATE Review Item (Instance)   *********
//*************************************************
function _updateReviewItemError(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateReviewItem: false,
        },
    };
}

function _updateReviewItemRequest(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateReviewItem: true,
        },
    };
}

function _updateReviewItemSuccess(state: ReviewQueuesState, payload: MessageReviewItemDto): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateReviewItem: false,
        },
        itemDetail: _enrichReviewItem(payload),
    };
}



//*************************************************
//*****   UPDATE Review Queue (Instance)   ********
//*************************************************
function _updateReviewQueueError(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateReviewQueue: false,
        },
    };
}

function _updateReviewQueueRequest(state: ReviewQueuesState): ReviewQueuesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateReviewQueue: true,
        },
    };
}

function _updateReviewQueueSuccess(state: ReviewQueuesState, payload: ReviewQueueDto): ReviewQueuesState {
    // create new list w updated queue
    const [ ...newQueueList ] = state.queueList;
    const updatedIndex = state.queueList.findIndex(x => x.id === payload.id);
    if (updatedIndex > -1) {
        newQueueList[updatedIndex] = payload;
    }

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateReviewQueue: false,
        },
        queueDetail: payload,
        queueList: newQueueList,
    };
}





//*************************************************
//*****   Helper Functions   **********************
//*************************************************
/**
 * _enrichReviewItem
 * desc: helper function to enrich review item with additional data
 * note: review item is the current item selected
 * @param  {array}  payload  Original Array
 * @return {object}          New object
 */
const _enrichReviewItem = (payload: MessageReviewItemDto): EnrichedReviewItem => {
    const RESERVED_REVIEW_FIELDS = journeysConstants.STEP.REVIEW.INPUTS_RESERVED;
    const commonRequestFields: ReviewItemRequestFieldDto[] = [];
    const reservedRequestFieldsMap: EnrichedReviewItem['reservedRequestFieldsMap'] = {};
    const commonResponseFields: ReviewItemResponseFieldDto[] = [];
    const reservedResponseFieldsMap: EnrichedReviewItem['reservedResponseFieldsMap'] = {};

    payload.requestFields.forEach((item) => {
        if (Object.values(RESERVED_REVIEW_FIELDS).includes(item.name)) {
            if (item.name === RESERVED_REVIEW_FIELDS.CREATED_DATE) {
                reservedRequestFieldsMap[item.name] = item.value ? getIntlDateTime(item.value) : null;
            } else {
                reservedRequestFieldsMap[item.name] = item;
            }
        } else {
            commonRequestFields.push(item);
        }
    });

    payload.responseFields.forEach((item) => {
        if (Object.values(RESERVED_REVIEW_FIELDS).includes(item.name)) {
            reservedResponseFieldsMap[item.name] = item;
        } else {
            commonResponseFields.push(item);
        }
    });

    return {
        ...payload,
        commonRequestFields,
        reservedRequestFieldsMap,
        commonResponseFields,
        reservedResponseFieldsMap
    };
};

/**
 * _enrichReviewQueue
 * desc: helper function to enrich review queues item with additional data
 * note: review queue is the current queue selected
 * @param  {object}  item  Original object
 * @return {object}        New Object
 */
const _enrichReviewQueue = (item: ReviewQueueDto): ReviewQueue => {
    return {
        ...item,
        availableItemCount: (item.meta && !Number.isNaN(item.meta.unreviewedItemCount) && !Number.isNaN(item.meta.assignedItemCount)) ? (item.meta.unreviewedItemCount - item.meta.assignedItemCount) : 0
    };
};

/**
 * _enrichReviewQueueList
 * desc: enrichs review queues items with additional data
 * note: review queue list is the collection of all queues
 * @param  {array || object}  payload  Original array
 * @return {object}                    New array
 */
const _enrichReviewQueueList = (payload: ReviewQueueDto[]): ReviewQueue[] => {
    return payload.map(item =>  _enrichReviewQueue(item));
};


export default reviewQueuesReducer as Reducer<ReviewQueuesState>;
